import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useHistory } from 'react-router-dom'
import { FleetPage, Notice } from '../../../../../shared/sso-workspace/fleet-page'
import { FleetGrid, useGridList } from '../../../../../shared/sso-workspace/fleet-grid'
import { TableAgClient } from '../../../../../shared/table-builder/table-ag-client'
import { ROW_HEIGHT_TWO_LINE } from '../../../../../shared/table-builder/table-ag'
import { ColumnFormat, ColumnMenu, ColumnText } from '../../../../../shared/table-builder/table-columns'
import { Menu, MenuItem } from '../../../../../shared/menu/menu'
import { IconAdd, IconCircleExclamation, IconDelete, IconPassword, IconRedo } from '../../../../../shared/icon/icon'
import { serverMessage } from '../../../../../shared/error/server-message'
import { ErrorReference } from '../../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../../shared/error/use-error-line'
import { agoText, dateText, utcText } from '../../../../../shared/date/local-time'
import { getAccess, listKeys } from '../api'
import { noteApiAccess } from '../use-api-access'
import { useKeyWrites } from '../key-access'
import { ACCESS_OFF_BANNER, NeverExpiresPill, StatusChip, bindingText, byWhom, isLive, keyStatus, prefixText, statusText } from '../key-words'
import { ApiKeyCreateDrawer } from './api-key-create-drawer'
import { useKeyActions } from './api-key-actions'
import { useAuthContext } from '../../../../../context/auth.context'
import { isKeyStaff } from '../grants-access'
import { PLATFORM_KEYS_NOTE, usePlatformOrganization } from '../platform-organization'
import { useRoleSysApiKey } from '../../../../../role/hook/role-sys'
import { ApiAccessToggle } from '../../../admin/organization/page/organization-edit/api-access-toggle'
import '../api-keys.scss'

// The organization's API keys (Phase D D6/D9/D11; plan Task 15), like
// GitHub's developer settings: every key, newest first, with its prefix,
// API role, status, last use and expiry; create (the secret shown once),
// rotate with a grace, revoke with an optional reason. A key's own page
// has its usage and request log.
//
// Partner API access is a switch only EducationXR staff turn (ruling 9):
// while it is off every key of the organization is refused, which the
// banner says, and no key can be made. The page is in the sidebar for
// whoever reads API keys, access on or off (owner 2026-10-23: "there isn't
// even an option in api keys to create one"): off, it says so; staff who
// may see the switch (SYS_API_KEY read) get it inline, and switch it with
// SYS_API_KEY update (the organization page's card, with its reason); anyone
// else is told to ask EducationXR.
//
// EducationXR's own organization's keys are platform keys
// (platform-organization.js): the page says so there.
//
// The house list page (CLAUDE.md "List page layout"; owner 2026-10-29: "stuff
// is running together"): Create API key is the Keys card menu's pinned button,
// with Download and Table settings in its ⋮; the card's sort chip says the
// order ("Sorted by Created, newest first"), so the card has no subtitle.
//
// The columns fit a 1280 px window beside the sidebar (owner 2026-10-29: at
// 1440 the grid still scrolled sideways and the action column covered
// Expires; measured on dev in a 1280 px frame, 799 px for the columns beside
// the action column). Every column is sized to its content (CLAUDE.md "Column
// widths", with AG Grid's 16 px cell padding each side), so the cells carry
// what read as more columns: the Name cell has the key's prefix under its name
// (and its system key after it, Phase E ruling E-4, when it has one) and its
// creation ("Created Oct 1, 2026 by ...") on hover, NAME_MAX wide at most
// (longer names ellipsize, and spare width widens it); the Status cell has the
// last use under the status, as the staff list's has. The Key, System key,
// Last used and Created columns stay, hidden, in Table Settings; Created stays
// the sort. A key bound to one record says so under its role ("Bound to LMS
// connection 7"), which the binding limits.
export const NAME_MAX = 280

const msOf = value => (value ? Date.parse(value) : 0)
// A column's sort by a number on the row; equal ones by id (newest last),
// so the order never depends on how the grid breaks ties.
export const byField = field => (a, b, nodeA, nodeB) => {
  const x = nodeA && nodeA.data ? nodeA.data : {}
  const y = nodeB && nodeB.data ? nodeB.data : {}
  return (x[field] || 0) - (y[field] || 0) || (x.id || 0) - (y.id || 0)
}

// The cells' words, flattened onto the row: the grid sorts, quick-filters
// and exports the field's value, so each column's field holds what it shows.
export const keyRow = key => {
  const status = keyStatus(key)
  return {
    ...key,
    status,
    prefixText: prefixText(key.prefix),
    // the Name cell shows the name and the prefix under it: both sort,
    // quick-filter ("which key is edxr_Nw9K…?") and read in its field
    nameText: [key.name, prefixText(key.prefix)].filter(Boolean).join(' '),
    roleName: (key.role && key.role.name) || '',
    statusText: statusText(status),
    lastUsedText: key.last_used_at ? agoText(key.last_used_at) : 'Never used',
    lastUsedMs: msOf(key.last_used_at),
    expiresText: status === 'revoked' ? 'Revoked' : key.expires_at ? dateText(key.expires_at) : 'Never',
    expiresMs: key.expires_at ? msOf(key.expires_at) : Number.MAX_SAFE_INTEGER,
    createdText: key.created_at ? dateText(key.created_at) : '',
    createdMs: msOf(key.created_at),
    createdByText: byWhom(key.created_by, key.created_by_support),
    // Phase E (ruling E-4): the id space its partner API calls write
    systemKeyText: key.system_key || '',
    // a key bound to one record (an LMS connection; D7-8) says which
    bindingText: bindingText(key)
  }
}

// The cells below are shared with an LMS connection's API keys panel
// (view/lms-admin/connections/connection-keys-panel.js), which lists the
// keys bound to that connection with the same columns.
// "Created Oct 1, 2026 by Olive Owner": the Name cell's hover.
export const createdLine = key => (key.createdText ? `Created ${key.createdText}${key.createdByText ? ` by ${key.createdByText}` : ''}` : '')

const NameCell = ({ data }) => {
  if (!data) return ''
  return (
    <span className="ak-name-cell" title={createdLine(data) || undefined}>
      <Link className="fw-semibold text-truncate" to={`/org/api-keys/${data.id}`} onClick={e => e.stopPropagation()}>{data.name}</Link>
      <span className="sw-cell-sub text-truncate"><span className="ak-prefix">{data.prefixText}</span>{data.systemKeyText ? ` · system key ${data.systemKeyText}` : ''}</span>
    </span>
  )
}
export const nameCell = cell => <NameCell data={cell.data} />
// The key's role, and under it the record it is bound to, if any.
const roleCell = cell => {
  const key = cell.data
  if (!key) return ''
  return (
    <span className="ak-stack">
      <span className="text-truncate">{key.roleName}</span>
      {key.bindingText && <span className="sw-cell-sub text-truncate">{key.bindingText}</span>}
    </span>
  )
}
// The status, and under it the last use: "Used 2 hours ago" (the exact time
// on hover), or "Never used".
const usedLine = key => (key.last_used_at ? <span title={utcText(key.last_used_at)}>{`Used ${key.lastUsedText}`}</span> : 'Never used')
export const statusCell = cell => (cell.data
  ? <span className="ak-stack">
    <span><StatusChip status={cell.data.status} /></span>
    <span className="sw-cell-sub text-truncate">{usedLine(cell.data)}</span>
  </span>
  : '')
export const lastUsedCell = cell => {
  const key = cell.data
  if (!key) return ''
  return key.last_used_at ? <span title={utcText(key.last_used_at)}>{key.lastUsedText}</span> : <span className="sw-hint">Never used</span>
}
export const expiresCell = cell => {
  const key = cell.data
  if (!key) return ''
  if (key.status === 'revoked') return <span className="sw-hint">Revoked</span>
  if (!key.expires_at) return <NeverExpiresPill />
  return <time dateTime={new Date(key.expires_at).toISOString()} title={utcText(key.expires_at)}>{key.expiresText}</time>
}

export const createdCell = cell => (cell.data ? <span title={utcText(cell.data.created_at)}>{cell.data.createdText}</span> : '')
const systemKeyCell = cell => (cell.data && cell.data.systemKeyText ? <span className="sw-mono">{cell.data.systemKeyText}</span> : '')

const FAILED = { Icon: IconCircleExclamation, title: "Couldn't load the keys", message: 'Use Reload below or try again shortly.' }

// A key's row menu: Rotate and Revoke, pinned, while the key is live and
// this person may (writes: useKeyWrites; actions: useKeyActions). Rotate is
// not offered where only EducationXR staff may (a key holding AI tools
// telemetry).
export const useKeyMenu = ({ canRotate, canDelete }, { askRotate, askRevoke }) => useMemo(() => key => (
  <Menu small>
    <MenuItem Icon={IconRedo} text="Rotate" pin show={canRotate(key) && isLive(key)} onClick={() => askRotate(key)} />
    <MenuItem Icon={IconDelete} text="Revoke" pin show={canDelete && isLive(key)} onClick={() => askRevoke(key)} />
  </Menu>
), [canRotate, canDelete, askRotate, askRevoke])

export const ApiKeyList = () => {
  const history = useHistory()
  const writes = useKeyWrites()
  const tableRef = useRef()
  const [rows, setRows] = useState(undefined)
  const [error, failure, setError] = useErrorLine()
  const [access, setAccess] = useState(null)
  const [creating, setCreating] = useState(false)
  const { state } = useAuthContext()
  // The inline switch is the organization page's card: shown with SYS_API_KEY
  // read, switched with update; never to an impersonated session.
  const sysApiKey = useRoleSysApiKey()
  const seesSwitch = isKeyStaff(state) && !!sysApiKey.accessRead
  const canSwitch = seesSwitch && !!sysApiKey.accessUpdate
  const organizationID = state && state.organizationID
  const platform = usePlatformOrganization({ keys: rows })

  const load = useCallback(async () => {
    try {
      const list = await listKeys()
      setError(null)
      setRows(Array.isArray(list) ? list.map(keyRow) : [])
    } catch (err) {
      setRows(current => (Array.isArray(current) && current.length ? current : null))
      setError(serverMessage(err, "The API keys couldn't be loaded."), err)
    }
  }, [setError])

  // Read afresh whenever the list opens (staff may have switched it), and
  // handed to the sidebar's entry too.
  const loadAccess = useCallback(() => getAccess().then(a => { setAccess(a || null); noteApiAccess(a || null) }, () => setAccess(null)), [])
  useEffect(() => { loadAccess() }, [loadAccess])

  const changed = useCallback(() => {
    load()
    loadAccess()
  }, [load, loadAccess])
  const actions = useKeyActions({ onChanged: changed })

  const created = answer => {
    setCreating(false)
    actions.reveal({ name: answer && answer.key ? answer.key.name : '', secret: answer && answer.secret })
    changed()
  }

  const gridList = useGridList(rows)
  const accessOff = !!access && access.enabled === false
  const atLimit = !!access && Number(access.max_keys) > 0 && Number(access.live_keys) >= Number(access.max_keys)
  const canOffer = writes.canCreate && !!access && !accessOff

  // The Keys card's menu (owner 2026-10-29): Create API key pinned (a button,
  // kept on a phone), Download and Table settings in the ⋮.
  const create = (
    <MenuItem key="create" Icon={IconAdd} text="Create API key" pin keepOnMobile show={canOffer} disabled={atLimit}
      title={atLimit ? 'Revoke a key to make another: this organization has the most live keys it can have.' : undefined}
      onClick={() => setCreating(true)} />
  )

  const menu = useKeyMenu(writes, actions)

  return (
    <FleetPage
      title="API keys"
      purpose="Keys let this organization's own systems call the EducationXR partner API. Each key acts with one API role, and its secret is shown only once."
      testId="api-keys-page">
      {platform.here && <Notice tone="ok">{PLATFORM_KEYS_NOTE}</Notice>}
      {accessOff && Number(access.live_keys) > 0 && <Notice tone="bad" role="alert">{ACCESS_OFF_BANNER}</Notice>}
      {accessOff && !(Number(access.live_keys) > 0) &&
        <Notice tone="warn">
          {canSwitch
            ? "Partner API access isn't switched on for this organization, so no key can be made yet. Switch it on below, then create a key."
            : seesSwitch
              ? "Partner API access isn't switched on for this organization, so no key can be made yet. Switching it on needs the API key update permission."
              : "Partner API access isn't switched on for this organization, so no key can be made yet. Ask EducationXR to switch it on."}
        </Notice>}
      {accessOff && seesSwitch && organizationID &&
        <div className="mb-3" data-testid="api-access-inline">
          <ApiAccessToggle organizationID={organizationID} onChanged={changed} />
        </div>}
      {atLimit && canOffer &&
        <Notice tone="warn">{`This organization has ${access.live_keys} live keys, the most an organization can have. Revoke one to make another.`}</Notice>}
      {writes.readOnlyReason && <Notice tone="warn">{writes.readOnlyReason}</Notice>}
      {error && <Notice tone="bad" role="alert">{error}<ErrorReference error={failure} /></Notice>}

      <FleetGrid title="Keys" tableRef={tableRef} exportName="api-keys" rowHeight={ROW_HEIGHT_TWO_LINE} menu={create}>
        <TableAgClient
          ref={tableRef}
          apiList={gridList}
          onReady={load}
          sortCol="createdText"
          sortDesc
          rowHeight={ROW_HEIGHT_TWO_LINE}
          onRowClicked={key => history.push(`/org/api-keys/${key.id}`)}
          emptyState={rows === null ? FAILED : {
            Icon: IconPassword,
            title: 'No API keys yet',
            message: canOffer
              ? 'Create a key for each system that calls the partner API. Its secret is shown once, when it is made.'
              : accessOff
                ? (canSwitch
                  ? 'Partner API access is off for this organization. Switch it on above, then create a key.'
                  : seesSwitch
                    ? 'Partner API access is off for this organization, and switching it on needs the API key update permission.'
                    : 'Partner API access is off for this organization, so no key can be made. Ask EducationXR to switch it on.')
                : 'Keys this organization makes show here.',
            ...(canOffer && !atLimit ? { action: { label: 'Create API key', Icon: IconAdd, onClick: () => setCreating(true) } } : {})
          }}
          noMatchState={{ message: 'No keys match. Clear the search.' }}>
          <ColumnFormat field="nameText" header="Name" render={nameCell} exportValue={key => key.name} stacked flex={2} textMax={NAME_MAX} sortable />
          <ColumnText field="prefixText" header="Key" fit="content" sortable hide />
          <ColumnFormat field="roleName" header="Role" render={roleCell} exportValue={key => key.roleName} stacked sortable />
          <ColumnFormat field="systemKeyText" header="System key" render={systemKeyCell} exportValue={key => key.systemKeyText} fit="content" sortable hide />
          <ColumnFormat field="statusText" header="Status" render={statusCell} exportValue={key => key.statusText} fit="content" stacked sortable />
          <ColumnFormat field="lastUsedText" header="Last used" render={lastUsedCell} comparator={byField('lastUsedMs')} sortKind="date" exportValue={key => key.last_used_at || 'never'} fit="content" sortable hide />
          <ColumnFormat field="expiresText" header="Expires" render={expiresCell} comparator={byField('expiresMs')} exportValue={key => key.expires_at || key.expiresText} fit="content" sortable />
          <ColumnFormat field="createdText" header="Created" render={createdCell} comparator={byField('createdMs')} sortKind="date" exportValue={key => key.created_at} fit="content" sortable hide />
          <ColumnMenu menu={menu} />
        </TableAgClient>
      </FleetGrid>

      {creating && <ApiKeyCreateDrawer open onClose={() => setCreating(false)} onCreated={created} />}
      {actions.dialogs}
    </FleetPage>
  )
}
